import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiConfig from '../../../config/apiConfig';

const PERSONA_TOKEN_KEY = 'foundations_persona_token';
const PERSONA_SESSION_KEY = 'foundations_persona_session';

const PersonaAccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const [step, setStep] = useState('idle'); // idle | otp_sent | verifying | error
  const [maskedEmail, setMaskedEmail] = useState('');
  const [personaLabel, setPersonaLabel] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-fire /exchange on mount when a token is present.
  useEffect(() => {
    if (!token) {
      setStep('error');
      setError('Missing access token. Open the link from your invitation email.');
      return;
    }
    requestOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${apiConfig.getBaseUrl()}/api/auth/persona/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        setStep('error');
        setError(data.message || data.error || 'Could not start sign-in. The link may have expired.');
        return;
      }
      setMaskedEmail(data.masked_email || '');
      setPersonaLabel(data.persona_label || '');
      setStep('otp_sent');
    } catch {
      setStep('error');
      setError('Unable to reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${apiConfig.getBaseUrl()}/api/auth/persona/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, otp: otpCode }),
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        setError(data.message || data.error || 'Verification failed.');
        return;
      }

      // Strict-scope persona JWT — separate localStorage namespace so it
      // doesn't collide with a coordinator session in another tab.
      localStorage.setItem(PERSONA_TOKEN_KEY, data.token);
      localStorage.setItem(PERSONA_SESSION_KEY, JSON.stringify(data.persona));

      const { session_id, persona_type } = data.persona;
      navigate(`/persona-assessment/${session_id}?persona=${persona_type}&locked=1`, { replace: true });
    } catch {
      setError('Unable to reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/risk-aperture-logo.svg" alt="Risk Aperture" style={logoStyle} />
          <p style={{ color: '#a8b2cc', margin: '1rem 0 0 0', fontSize: '1rem' }}>
            Assessment Access
          </p>
          {personaLabel && (
            <p style={{ color: '#7a8aaa', margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
              Signing in as {personaLabel}
            </p>
          )}
        </div>

        {error && <div role="alert" style={errorStyle}>{error}</div>}

        {step === 'idle' && (
          <p style={{ color: '#a8b2cc', textAlign: 'center' }}>
            Verifying your invitation link…
          </p>
        )}

        {step === 'otp_sent' && (
          <>
            <p style={{ color: '#a8b2cc', textAlign: 'center', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
              We sent a 6-digit code to <strong style={{ color: '#c0c8d8' }}>{maskedEmail || 'your email'}</strong>
            </p>
            <form onSubmit={handleVerify} style={formStyle}>
              <div>
                <label htmlFor="persona-otp" style={labelStyle}>Verification Code</label>
                <input
                  id="persona-otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  placeholder="000000"
                  style={{ ...inputStyle, textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                style={{
                  ...primaryButtonStyle,
                  opacity: (loading || otpCode.length !== 6) ? 0.5 : 1,
                  cursor: (loading || otpCode.length !== 6) ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Verifying…' : 'Continue to Assessment'}
              </button>
            </form>
            <button
              onClick={() => { setOtpCode(''); setError(''); requestOtp(); }}
              disabled={loading}
              style={{ ...linkBtnStyle, marginTop: '1rem' }}
            >
              Resend code
            </button>
          </>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#a8b2cc', fontSize: '0.9rem' }}>
              If you believe this is a mistake, contact the coordinator who sent you this invitation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const pageStyle = {
  minHeight: '100vh',
  background: 'radial-gradient(ellipse at bottom, #172a45 0%, #0a192f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};
const cardStyle = {
  width: '100%',
  maxWidth: '460px',
  background: 'rgba(255, 255, 255, 0.06)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '16px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
  padding: '2.5rem',
};
const logoStyle = { height: 80, width: 'auto', filter: 'brightness(0) invert(1)' };
const formStyle = { display: 'flex', flexDirection: 'column', gap: '1.25rem' };
const labelStyle = { display: 'block', fontSize: '0.875rem', color: '#a8b2cc', marginBottom: '0.5rem' };
const inputStyle = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '8px',
  color: '#e6f1ff',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};
const primaryButtonStyle = {
  width: '100%',
  padding: '0.85rem',
  background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)',
  border: 'none',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  fontFamily: 'inherit',
};
const errorStyle = {
  marginBottom: '1.5rem',
  padding: '0.75rem 1rem',
  background: 'rgba(255, 68, 68, 0.08)',
  border: '1px solid rgba(255, 68, 68, 0.25)',
  color: '#ff4444',
  borderRadius: '8px',
  fontSize: '0.875rem',
};
const linkBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#00d9ff',
  cursor: 'pointer',
  padding: 0,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  textDecoration: 'underline',
};

export default PersonaAccess;
